import { useEffect, useRef } from 'react'
import type { Location } from 'react-router'
import { useLocation } from 'react-router'

import { safeDataLayer } from '@modules/utils/gtm'

export const useRouteUtils = () => {
  const location = useLocation()

  const getFullPath = (): string => {
    return location.pathname + location.search
  }

  const getSearchQuery = (): URLSearchParams => {
    return new URLSearchParams(location.search)
  }

  return { getFullPath, getSearchQuery }
}

export const getSearchQuery = (location: Location): URLSearchParams => {
  return new URLSearchParams(location.search)
}

/**
 * ルート変更のたびにGTMへページビュー（`routeChange`）を1回送る。
 *
 * 依存はルートを表す値（`pathname` / `search`）だけにする。以前は `useRouteUtils()` が
 * 毎レンダー作り直す `getFullPath` を依存に入れていたため、URLが変わっていなくても
 * このフックを呼ぶコンポーネントが再描画されるたびに同じページビューが積まれ、GA4の
 * ページビュー数が実態より多くなっていた（state-lifecycle-audit.md §3.13）。
 */
export const useRouteChangeMiddleware = () => {
  const location = useLocation()
  const prevLocationRef = useRef<
    | {
        location: Location
        fullPath: string
      }
    | undefined
  >(undefined)
  // effectの中で参照するlocationは常に最新のものにする（依存には入れない）
  const locationRef = useRef(location)
  locationRef.current = location

  const { pathname, search } = location
  useEffect(() => {
    const current = locationRef.current
    const fullPath = current.pathname + current.search
    safeDataLayer({
      event: 'routeChange',
      pageType: 'PageView',
      pageUrl: fullPath,
      pagePath: current.pathname,
      pageTitle: document.title,
      fromPageUrl: prevLocationRef.current?.fullPath,
      fromPagePath: prevLocationRef.current?.location.pathname,
    })

    prevLocationRef.current = { location: current, fullPath }
  }, [pathname, search])
}

export const convertSearchParams = (params: Record<string, unknown>): string => {
  return Object.entries(params)
    .flatMap(([k, v]) => {
      if (v == null) return []
      if (Array.isArray(v)) {
        return v.map(val => `${encodeURIComponent(k)}[]=${encodeURIComponent(String(val))}`)
      }
      if (
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean' ||
        typeof v === 'bigint'
      ) {
        return `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
      }
      return []
    })
    .join('&')
}

if (import.meta.vitest) {
  const { test, expect } = import.meta.vitest

  test('convertSearchParams', () => {
    const params = {
      name: 'John',
      age: '30',
    }
    expect(convertSearchParams(params)).toBe('name=John&age=30')
  })

  test('convertSearchParams with array', () => {
    const params = {
      name: 'John',
      skills: ['React', 'TypeScript'],
    }
    expect(decodeURIComponent(convertSearchParams(params))).toBe(
      'name=John&skills[]=React&skills[]=TypeScript',
    )
  })
  test('convertSearchParams with bigint', () => {
    const params = {
      name: 'John',
      age: BigInt(30),
    }
    expect(decodeURIComponent(convertSearchParams(params))).toBe('name=John&age=30')
  })

  test('convertSearchParams with undefined and null', () => {
    const params = {
      name: 'John',
      undefined,
      null: null,
    } as any
    expect(decodeURIComponent(convertSearchParams(params))).toBe('name=John')
  })
}
